import { HttpStatusCode } from "axios";
import LoadingSpinner from "components/components/LoadingSpinner/LoadingSpinner";
import { SpinnerContainer } from "components/components/LoadingSpinner/style";
import RemitApiCredentialsDetails from "components/components/Remit/AuthenticatedView/RemitApiCredentials/RemitApiCredentialsDetails";
import RemitApiCredentialsLanding from "components/components/Remit/AuthenticatedView/RemitApiCredentials/RemitApiCredentialsLanding";
import { useErrorContext } from "contexts/ErrorContext";
import useRemitApiCredentials from "hooks/useRemitApiCredentials";
import { RequestStatus } from "hooks/useRequest";
import React, { useEffect } from "react";

import { SpinnerSection } from "./style";

const RemitApiCredentialsLoading: React.FC = () => (
  <SpinnerSection>
    <SpinnerContainer>
      <LoadingSpinner isLoading />
      <span>
        Please wait while we fetch your REMIT API credentials. This may take up
        to a minute.
      </span>
    </SpinnerContainer>
  </SpinnerSection>
);

const RemitApiCredentials: React.FC = () => {
  const { registerError } = useErrorContext();
  const {
    credentials,
    createdSecret,
    getCredentials,
    createCredentials,
    createSecret,
    deleteSecret,
    getCredentialsStatus,
    createCredentialsStatus,
    createSecretStatus,
    deleteSecretStatus,
    getCredentialsErrorCode,
  } = useRemitApiCredentials();

  useEffect(() => {
    getCredentials();
  }, [getCredentials]);

  const hasGetCredentialsErrored =
    getCredentialsStatus === RequestStatus.ERRORED &&
    getCredentialsErrorCode !== HttpStatusCode.NotFound;
  const hasMutationErrored =
    createCredentialsStatus === RequestStatus.ERRORED ||
    createSecretStatus === RequestStatus.ERRORED ||
    deleteSecretStatus === RequestStatus.ERRORED;
  const hasErrored = hasGetCredentialsErrored || hasMutationErrored;

  useEffect(() => {
    if (hasErrored) {
      registerError();
    }
  }, [hasErrored, registerError]);

  if (credentials) {
    return (
      <RemitApiCredentialsDetails
        data={credentials}
        createdSecret={createdSecret}
        fetchCredentials={getCredentials}
        fetchCredentialsStatus={getCredentialsStatus}
        requestNewSecret={createSecret}
        requestNewSecretStatus={createSecretStatus}
        deleteSecret={deleteSecret}
        deleteSecretStatus={deleteSecretStatus}
      />
    );
  }

  const isLoading =
    getCredentialsStatus === RequestStatus.IN_PROGRESS ||
    createCredentialsStatus === RequestStatus.IN_PROGRESS;
  const isInPreRequestState =
    getCredentialsStatus === RequestStatus.SUCCESSFUL_OR_NOT_STARTED &&
    createCredentialsStatus === RequestStatus.SUCCESSFUL_OR_NOT_STARTED;

  if (isLoading || hasErrored || isInPreRequestState) {
    return <RemitApiCredentialsLoading />;
  }

  return (
    <RemitApiCredentialsLanding
      requestCredentials={createCredentials}
      requestStatus={createCredentialsStatus}
    />
  );
};

export default RemitApiCredentials;
